/**
 * Poll the DOM until a tour-step target shows up.
 *
 * react-joyride's `continuous` mode fires `TARGET_NOT_FOUND` the moment a
 * step's target is missing, and our `TourSpotlight` reaction is to
 * `advance()`. Without a wait between flipping the wizard's section and
 * looking for the target, the wizard's lazy-loaded chunks (each editor
 * section is `React.lazy`) lose the race and Joyride cascades through
 * every subsequent step until it lands on a target that's universally
 * present (`body` on the wrap-up beat). That is why the full walkthrough
 * used to skip from slide 1 straight to slide 26.
 *
 * `waitForTourTarget` is the gate the previous code missed: TourProvider
 * uses it to delay committing the next `stepIndex` to Joyride until the
 * target is in the DOM. Polls every `intervalMs` (default 50ms) with a
 * `timeoutMs` ceiling (default 3.5s). `body` and other "always present"
 * anchors short-circuit. Resolves `true` once the selector matches,
 * `false` if it never does — TourProvider then commits anyway so a stale
 * catalogue selector can't strand the operator on a single beat.
 *
 * Dependency injection keeps the helper unit-testable from a plain Node
 * runner: callers can pass a synchronous `match` probe + a fake
 * `schedule` / `now` instead of touching a real DOM.
 */

export const TOUR_TARGET_DEFAULT_TIMEOUT_MS = 3500;
export const TOUR_TARGET_DEFAULT_INTERVAL_MS = 50;

/** Targets that are always considered ready (no need to poll). */
export function isImmediateTourTarget(target) {
  if (!target || typeof target !== 'string') return true;
  if (target === 'body') return true;
  if (target === 'html') return true;
  return false;
}

/**
 * Return a Promise<boolean> that resolves once `match(target)` returns
 * truthy or the timeout elapses. The returned promise carries a
 * `.cancel()` method so a caller (e.g. a useEffect cleanup) can abandon
 * an in-flight wait without leaking timers.
 *
 * Options:
 *   - timeoutMs   how long to keep polling before giving up (3500)
 *   - intervalMs  poll cadence (50)
 *   - match       sync probe; defaults to `document.querySelector`-based
 *                 lookup but tests can inject a deterministic fake
 *   - now         clock source; tests inject fake clocks
 *   - schedule    timer factory; tests inject fake setTimeout
 *   - cancelTimer pair for `schedule`; tests inject fake clearTimeout
 */
export function waitForTourTarget(target, opts = {}) {
  const {
    timeoutMs = TOUR_TARGET_DEFAULT_TIMEOUT_MS,
    intervalMs = TOUR_TARGET_DEFAULT_INTERVAL_MS,
    match,
    now = () => Date.now(),
    schedule = (fn, ms) => setTimeout(fn, ms),
    cancelTimer = (handle) => clearTimeout(handle),
  } = opts;

  const probe = match || ((sel) => {
    if (typeof document === 'undefined') return false;
    try {
      return !!document.querySelector(sel);
    } catch (_) {
      // Bad selector strings shouldn't take the host UI down; treat as
      // permanently unmatchable so the wait short-circuits via timeout.
      return false;
    }
  });

  let cancelled = false;
  let handle = null;

  const promise = new Promise((resolve) => {
    if (isImmediateTourTarget(target)) {
      resolve(true);
      return;
    }
    if (probe(target)) {
      resolve(true);
      return;
    }

    const start = now();
    const tick = () => {
      if (cancelled) return;
      handle = null;
      if (probe(target)) {
        resolve(true);
        return;
      }
      if (now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      handle = schedule(tick, intervalMs);
    };
    handle = schedule(tick, intervalMs);
  });

  return Object.assign(promise, {
    cancel() {
      if (cancelled) return;
      cancelled = true;
      if (handle != null) {
        try { cancelTimer(handle); } catch (_) { /* never throw from cancel */ }
        handle = null;
      }
    },
  });
}
